// src/components/VideoPlayer.js
import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/styles.css";

const FREE_LIMIT_SECONDS = 60;
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

const VideoPlayer = ({ videoUrl, user, onProgress }) => {
  const videoRef     = useRef(null);
  const containerRef = useRef(null);
  const hideTimer    = useRef(null);
  const navigate     = useNavigate();

  const [showOverlay,   setShowOverlay]   = useState(false);
  const [isPlaying,     setIsPlaying]     = useState(false);
  const [isMuted,       setIsMuted]       = useState(false);
  const [isFullscreen,  setIsFullscreen]  = useState(false);
  const [isPiP,         setIsPiP]         = useState(false);
  const [volume,        setVolume]        = useState(1);
  const [currentTime,   setCurrentTime]   = useState(0);
  const [duration,      setDuration]      = useState(0);
  const [showControls,  setShowControls]  = useState(true);
  const [buffered,      setBuffered]      = useState(0);
  const [speed,         setSpeed]         = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [flashIcon,     setFlashIcon]     = useState(null);

  const flash = (icon) => {
    setFlashIcon(icon);
    setTimeout(() => setFlashIcon(null), 600);
  };

  // ── Tiempo ──────────────────────────────────────────────
  const handleTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    setCurrentTime(v.currentTime);
    if (onProgress && v.duration) {
      onProgress(Math.round((v.currentTime / v.duration) * 100));
    }
    if (!user && v.currentTime >= FREE_LIMIT_SECONDS) {
      v.pause();
      setIsPlaying(false);
      setShowOverlay(true);
    }
  }, [user, onProgress]);

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleProgress = () => {
    const v = videoRef.current;
    if (!v || !v.duration || !v.buffered.length) return;
    setBuffered((v.buffered.end(v.buffered.length - 1) / v.duration) * 100);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.addEventListener("timeupdate",     handleTimeUpdate);
    v.addEventListener("loadedmetadata", handleLoadedMetadata);
    v.addEventListener("progress",       handleProgress);
    v.addEventListener("play",  () => setIsPlaying(true));
    v.addEventListener("pause", () => setIsPlaying(false));
    v.addEventListener("enterpictureinpicture", () => setIsPiP(true));
    v.addEventListener("leavepictureinpicture", () => setIsPiP(false));
    return () => {
      v.removeEventListener("timeupdate",     handleTimeUpdate);
      v.removeEventListener("loadedmetadata", handleLoadedMetadata);
      v.removeEventListener("progress",       handleProgress);
    };
  }, [handleTimeUpdate]);

  // ── Ocultar controles ────────────────────────────────────
  const showControlsTemp = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    if (isPlaying) {
      hideTimer.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [isPlaying]);

  // ── Teclado ──────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (!containerRef.current) return;
      // Solo activar si el player es visible en viewport
      const rect = containerRef.current.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      // No interferir con inputs
      if (["INPUT","TEXTAREA","SELECT"].includes(e.target.tagName)) return;

      switch (e.code) {
        case "Space":
        case "KeyK":
          e.preventDefault();
          togglePlay();
          break;
        case "ArrowLeft":
          e.preventDefault();
          skip(-10);
          flash("« 10s");
          break;
        case "ArrowRight":
          e.preventDefault();
          skip(10);
          flash("10s »");
          break;
        case "ArrowUp":
          e.preventDefault();
          setVolume(v => { const nv = Math.min(1, v + 0.1); if(videoRef.current) videoRef.current.volume = nv; return nv; });
          break;
        case "ArrowDown":
          e.preventDefault();
          setVolume(v => { const nv = Math.max(0, v - 0.1); if(videoRef.current) videoRef.current.volume = nv; return nv; });
          break;
        case "KeyM":
          e.preventDefault();
          toggleMute();
          break;
        case "KeyF":
          e.preventDefault();
          toggleFullscreen();
          break;
        default: break;
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isPlaying]); // eslint-disable-line

  // ── Acciones ─────────────────────────────────────────────
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v || showOverlay) return;
    if (v.paused) { v.play(); flash("▶"); } else { v.pause(); flash("⏸"); }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const changeVolume = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      setIsMuted(val === 0);
    }
  };

  const seek = (e) => {
    if (!videoRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pct * duration;
  };

  const skip = (secs) => {
    if (videoRef.current) videoRef.current.currentTime += secs;
  };

  const changeSpeed = (s) => {
    setSpeed(s);
    if (videoRef.current) videoRef.current.playbackRate = s;
    setShowSpeedMenu(false);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const togglePiP = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await v.requestPictureInPicture();
      }
    } catch (_) {}
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  const progressPct = duration ? (currentTime / duration) * 100 : 0;
  const pipSupported = typeof document !== "undefined" && "pictureInPictureEnabled" in document;

  return (
    <div
      ref={containerRef}
      className={`vp-container ${showControls ? "vp-controls-visible" : "vp-controls-hidden"}`}
      onMouseMove={showControlsTemp}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      onClick={togglePlay}
    >
      <video
        ref={videoRef}
        className="vp-video"
        src={videoUrl}
        preload="metadata"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Flash de acción central */}
      {flashIcon && (
        <div className="vp-flash-icon">{flashIcon}</div>
      )}

      {/* Overlay "Regístrate" */}
      {showOverlay && (
        <div className="vp-gate-overlay">
          <div className="vp-gate-box">
            <div className="vp-gate-icon">🔒</div>
            <h3>Contenido completo para miembros</h3>
            <p>Regístrate gratis para ver esta y todas las producciones de AIVEO sin límite.</p>
            <div className="vp-gate-actions">
              <button className="btn-primary" onClick={(e) => { e.stopPropagation(); navigate("/register"); }}>
                Crear cuenta gratis
              </button>
              <button className="btn-outline-icon" onClick={(e) => { e.stopPropagation(); navigate("/login"); }}>
                Iniciar sesión
              </button>
            </div>
            <p className="vp-gate-note">Has visto 1 minuto de muestra gratuita</p>
          </div>
        </div>
      )}

      {/* Controles */}
      {!showOverlay && (
        <div className="vp-controls" onClick={(e) => e.stopPropagation()}>
          {/* Barra de progreso */}
          <div className="vp-progress-area" onClick={seek}>
            <div className="vp-progress-track">
              <div className="vp-progress-buffered" style={{ width: `${buffered}%` }} />
              <div className="vp-progress-fill"     style={{ width: `${progressPct}%` }} />
              <div className="vp-progress-thumb"    style={{ left: `${progressPct}%` }} />
            </div>
          </div>

          {/* Fila de botones */}
          <div className="vp-controls-row">
            <div className="vp-controls-left">
              <button className="vp-btn" onClick={togglePlay} title="Play/Pausa (Space)">
                {isPlaying ? "⏸" : "▶"}
              </button>
              <button className="vp-btn" onClick={() => { skip(-10); flash("« 10s"); }} title="Retroceder 10s (←)">⏪</button>
              <button className="vp-btn" onClick={() => { skip(10);  flash("10s »"); }} title="Avanzar 10s (→)">⏩</button>
              <button className="vp-btn" onClick={toggleMute} title="Silenciar (M)">
                {isMuted || volume === 0 ? "🔇" : volume < 0.5 ? "🔉" : "🔊"}
              </button>
              <input
                type="range"
                className="vp-volume-slider"
                min="0" max="1" step="0.05"
                value={isMuted ? 0 : volume}
                onChange={changeVolume}
                onClick={(e) => e.stopPropagation()}
                title="Volumen (↑↓)"
              />
              <span className="vp-time">{fmt(currentTime)} / {fmt(duration)}</span>
            </div>

            <div className="vp-controls-right">
              {/* Velocidad */}
              <div className="vp-speed-wrap">
                <button
                  className="vp-btn vp-speed-btn"
                  onClick={(e) => { e.stopPropagation(); setShowSpeedMenu(!showSpeedMenu); }}
                  title="Velocidad"
                >
                  {speed}x
                </button>
                {showSpeedMenu && (
                  <div className="vp-speed-menu" onClick={(e) => e.stopPropagation()}>
                    {SPEEDS.map(s => (
                      <button
                        key={s}
                        className={`vp-speed-option ${s === speed ? "active" : ""}`}
                        onClick={() => changeSpeed(s)}
                      >
                        {s}x
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* PiP */}
              {pipSupported && (
                <button className="vp-btn" onClick={togglePiP} title="Picture in Picture">
                  {isPiP ? "⊡" : "⧉"}
                </button>
              )}

              {/* Pantalla completa */}
              <button className="vp-btn" onClick={toggleFullscreen} title="Pantalla completa (F)">
                {isFullscreen ? "⊠" : "⛶"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leyenda de atajos (solo visible cuando los controles están visibles y pausa) */}
      {showControls && !isPlaying && !showOverlay && (
        <div className="vp-shortcuts-hint">
          Space · ← → · M · F
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
